// src/components/EditStudyModal.jsx
import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { DISEASE_OPTIONS, normalizeDisease, diagnosisLabel } from "../constants/diseaseOptions";
import BackupCrcInput from "./study/BackupCrcInput";
import LeadCrcInput from "./study/LeadCrcInput";
import { UI_REVAMP_ENABLED } from "../config/featureFlags";
import { backupCrcRows, normalizeBackupCrcs } from "../services/backupCrc";
import {
  X,
  Save,
  CalendarDays,
  FileText,
  ClipboardList,
  UserCircle2,
  Building2,
  Target,
  CheckCircle2,
  AlertCircle,
  Landmark,
  IdCard,
  ChevronDown,
  Activity,
} from "lucide-react";

const milestoneKeys = [
  "CDA","FEASIBILITY","SQV_DATE","IRB_SUBMISSION","SIV","ISF_SETUP","1572","FDF",
  "CREDENTIALS","PSP","IB_AOR","SCF","TL","DOA","SIV_FOLLOWUP","SDA","SRP","COI","GREENLIGHT","SSC","COV"
];

const numericKeys = ["GOAL", "ENROLLED", "SCREEN_FAILURES"];
const STATUS_OPTIONS = ["ACTIVE", "ACTIVE/NR", "START-UP", "CLOSED"];
const coreTextKeys = [
  "NAME", "SPONSOR", "SITE_ID", "STATUS", "LEAD_CRC", "SUBJECT_PAYMENT_METHOD"
];

// BUDGET_ACCEPTED is special → boolean toggle
const booleanKeys = ["BUDGET_ACCEPTED"];

const EditStudyModal = ({ study, onSave, onClose }) => {
  const initial = useMemo(
    () => ({
      ...study,
      // ensure these exist in the form state
      STUDY_ID: study?.STUDY_ID || "",
      CTA: study?.CTA || "",
      SSC: study?.SSC || "",
      "Paid # of SF": study?.["Paid # of SF"] || "",
      INCLUSION: study?.INCLUSION || "",
      EXCLUSION: study?.EXCLUSION || "",
      Comments: study?.Comments || "",

      COV: study?.COV || "",
      SCREEN_FAILURES: study?.SCREEN_FAILURES ?? 0,
    }),
    [study]
  );

  const [formData, setFormData] = useState(initial);

  useEffect(() => setFormData(initial), [initial]);

  // Backup CRCs are edited as rows (blanks kept while typing) and cleaned into
  // the stored BACKUP_CRC list on save.
  const [backupRows, setBackupRows] = useState(() => backupCrcRows(study?.BACKUP_CRC));
  useEffect(() => setBackupRows(backupCrcRows(study?.BACKUP_CRC)), [study]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Boolean toggles
    if (booleanKeys.includes(name)) {
      setFormData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
      return;
    }

    // Numeric fields
    if (numericKeys.includes(name)) {
      const num = value === "" ? "" : Number(value);
      setFormData((prev) => ({ ...prev, [name]: Number.isNaN(num) ? prev[name] : num }));
      return;
    }

    // Dates & plain text
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleSubmit = () => {
  const cleanedData = Object.fromEntries(
    Object.entries(formData).filter(([_, v]) => v !== "")
  );
  if (cleanedData.DISEASE) {
  cleanedData.DISEASE = normalizeDisease(cleanedData.DISEASE);
}
  // Write BACKUP_CRC only when there is something to say: a list to save, or a
  // study that already had one (so removing every backup really clears it).
  // A study that never had backups gets no new field from an unrelated edit.
  const backups = normalizeBackupCrcs(backupRows);
  if (backups.length || study?.BACKUP_CRC !== undefined) {
    cleanedData.BACKUP_CRC = backups;
  } else {
    delete cleanedData.BACKUP_CRC;
  }

  console.log("Saving:", cleanedData);

  onSave(cleanedData);
  onClose();
};

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 font-[Manrope]"
        onMouseDown={handleBackdropClick}
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-study-title"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        {/* Modal card */}
        <motion.div
          onMouseDown={(e) => e.stopPropagation()}
          initial={{ y: 18, scale: 0.98, opacity: 0 }}
          animate={{ y: 0, scale: 1, opacity: 1 }}
          exit={{ y: 18, scale: 0.98, opacity: 0 }}
          transition={{ type: "spring", stiffness: 260, damping: 22 }}
          className="relative w-full max-w-5xl max-h-[90vh] overflow-hidden rounded-2xl border border-white/10 bg-[#0c0826]/95 backdrop-blur-2xl shadow-[0_30px_80px_-20px_rgba(124,58,237,0.6)] flex flex-col text-slate-100"
        >
          {/* Top accent line */}
          <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-purple-400 via-fuchsia-400 to-blue-400 opacity-80 pointer-events-none z-10" />
          {/* Background glow */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-purple-600/20 blur-[140px]" />
            <div className="absolute -bottom-32 -left-32 w-[500px] h-[500px] rounded-full bg-blue-600/20 blur-[140px]" />
          </div>

          {/* Header */}
          <div className="relative px-5 py-3.5 border-b border-white/10 flex items-center gap-3 shrink-0">
            <div className="relative shrink-0">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500 to-blue-500 rounded-xl blur-md opacity-70" />
              <div className="relative w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500/30 via-indigo-500/30 to-blue-500/30 border border-white/15 ring-1 ring-white/20 flex items-center justify-center">
                <ClipboardList className="w-5 h-5 text-white" strokeWidth={2.2} />
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.25em] text-purple-300/80 font-semibold leading-tight">
                Study Editor
              </div>
              <h2
                id="edit-study-title"
                className="text-base font-semibold tracking-tight text-white leading-tight truncate"
              >
                {formData?.NAME ? formData.NAME : "Unnamed Study"}
              </h2>
            </div>
            {formData?.STUDY_ID && (
              <span className="hidden sm:inline-flex items-center px-2 py-1 rounded-md bg-white/[0.04] border border-white/10 text-[10px] font-mono tracking-wider text-purple-200">
                {formData.STUDY_ID}
              </span>
            )}
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg bg-white/[0.04] border border-white/10 hover:bg-white/10 hover:border-white/20 flex items-center justify-center text-slate-300 hover:text-white transition shrink-0"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Body */}
          <div className="relative overflow-y-auto flex-1 px-5 py-4 space-y-3">
            {/* Core Info */}
            <Section
              title="Core Information"
              subtitle="Primary identifiers and configuration"
              icon={<Landmark className="w-3.5 h-3.5 text-purple-300" />}
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {/* STUDY_ID */}
                <Field
                  label="Study ID"
                  name="STUDY_ID"
                  value={formData.STUDY_ID || ""}
                  onChange={handleChange}
                  icon={<IdCard className="w-4 h-4 text-slate-500" />}
                  placeholder="e.g., STUDY-0001"
                />
                {/* NAME */}
                <Field
                  label="Study Name"
                  name="NAME"
                  value={formData.NAME || ""}
                  onChange={handleChange}
                  icon={<FileText className="w-4 h-4 text-slate-500" />}
                  placeholder="e.g., COPD-1234, Asthma Study"
                />
                {/* DISEASE */}
                <label className="block">
                  <span className="block text-[10px] uppercase tracking-wider font-medium text-slate-400 mb-1">
                    Disease
                  </span>
                  <div className="relative">
                    <Activity className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
                    <select
                      name="DISEASE"
                      value={formData.DISEASE || ""}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          DISEASE: normalizeDisease(e.target.value),
                        }))
                      }
                      className="w-full appearance-none rounded-xl bg-white/[0.04] border border-white/10 pl-9 pr-9 py-2 text-sm text-slate-100 hover:bg-white/[0.06] focus:outline-none focus:border-purple-400/50 focus:ring-2 focus:ring-purple-500/20 cursor-pointer transition"
                    >
                      <option value="" className="bg-[#0b0820] text-slate-100">
                        Select disease
                      </option>
                      {DISEASE_OPTIONS.map((d) => (
                        <option
                          key={d}
                          value={d}
                          className="bg-[#0b0820] text-slate-100"
                        >
                          {diagnosisLabel(d)}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                  </div>
                </label>
                {/* SPONSOR */}
                <Field
                  label="Sponsor"
                  name="SPONSOR"
                  value={formData.SPONSOR || ""}
                  onChange={handleChange}
                  icon={<FileText className="w-4 h-4 text-slate-500" />}
                  placeholder="e.g., Pfizer, AstraZeneca"
                />
                {/* SITE_ID */}
                <Field
                  label="Site ID"
                  name="SITE_ID"
                  value={formData.SITE_ID || ""}
                  onChange={handleChange}
                  icon={<Building2 className="w-4 h-4 text-slate-500" />}
                  placeholder="e.g., 001"
                />
                {/* STATUS */}
                <SelectField
                  label="Status"
                  name="STATUS"
                  value={formData.STATUS || ""}
                  onChange={handleChange}
                  options={STATUS_OPTIONS}
                />
                {/* LEAD_CRC. UI revamp: picked from the roster, like Backup CRC
                    (still stored as a string). */}
                {UI_REVAMP_ENABLED ? (
                  <LeadCrcInput
                    value={formData.LEAD_CRC || ""}
                    onChange={(name) => setFormData((prev) => ({ ...prev, LEAD_CRC: name }))}
                  />
                ) : (
                <Field
                  label="Lead CRC"
                  name="LEAD_CRC"
                  value={formData.LEAD_CRC || ""}
                  onChange={handleChange}
                  icon={<UserCircle2 className="w-4 h-4 text-slate-500" />}
                  placeholder="e.g., Jane Doe"
                />
                )}
                {/* BACKUP_CRC — zero or more, stored as a list */}
                <BackupCrcInput rows={backupRows} onChange={setBackupRows} />
                {/* SUBJECT_PAYMENT_METHOD */}
                <Field
                  label="Subject Payment Method"
                  name="SUBJECT_PAYMENT_METHOD"
                  value={formData.SUBJECT_PAYMENT_METHOD || ""}
                  onChange={handleChange}
                  icon={<FileText className="w-4 h-4 text-slate-500" />}
                  placeholder="e.g., ClinCard, Gift Card"
                />
                {/* TRAVEL — free text, e.g. the travel budget */}
                <Field
                  label="Travel"
                  name="TRAVEL"
                  value={formData.TRAVEL || ""}
                  onChange={handleChange}
                  icon={<FileText className="w-4 h-4 text-slate-500" />}
                  placeholder="e.g., travel budget"
                />
                {/* CTA */}
                <Field
                  label="CTA"
                  name="CTA"
                  value={formData.CTA || ""}
                  onChange={handleChange}
                  icon={<FileText className="w-4 h-4 text-slate-500" />}
                  placeholder="e.g., Yes / No / Notes"
                />
                {/* Paid # of SF */}
                <Field
                  label="Paid # of SF"
                  name="Paid # of SF"
                  value={formData["Paid # of SF"] || ""}
                  onChange={handleChange}
                  icon={<FileText className="w-4 h-4 text-slate-500" />}
                  placeholder="e.g., 3 / 4 / Paid count"
                />
                {/* BUDGET_ACCEPTED toggle */}
                <ToggleField
                  label="Budget Accepted"
                  name="BUDGET_ACCEPTED"
                  checked={
                    !!(
                      formData.BUDGET_ACCEPTED === true ||
                      formData.BUDGET_ACCEPTED === "true" ||
                      formData.BUDGET_ACCEPTED === "Yes"
                    )
                  }
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      BUDGET_ACCEPTED: e.target.checked,
                    }))
                  }
                />
                {/* Numeric: GOAL, ENROLLED, SCREEN_FAILURES */}
                <NumberField
                  label="Enrollment Goal"
                  name="GOAL"
                  value={formData.GOAL ?? ""}
                  onChange={handleChange}
                  icon={<Target className="w-4 h-4 text-slate-500" />}
                />
                <NumberField
                  label="Enrolled"
                  name="ENROLLED"
                  value={formData.ENROLLED ?? ""}
                  onChange={handleChange}
                  icon={<CheckCircle2 className="w-4 h-4 text-slate-500" />}
                />
                <NumberField
                  label="Screen Failures"
                  name="SCREEN_FAILURES"
                  value={formData.SCREEN_FAILURES ?? ""}
                  onChange={handleChange}
                  icon={<AlertCircle className="w-4 h-4 text-slate-500" />}
                />
              </div>
            </Section>

            {/* Criteria */}
            <Section
              title="Eligibility Criteria"
              subtitle="Define inclusion and exclusion"
              icon={<ClipboardList className="w-3.5 h-3.5 text-purple-300" />}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <TextAreaField
                  label="Inclusion Criteria"
                  name="INCLUSION"
                  value={formData.INCLUSION}
                  onChange={handleChange}
                  rows={5}
                  placeholder="List inclusion points…"
                />
                <TextAreaField
                  label="Exclusion Criteria"
                  name="EXCLUSION"
                  value={formData.EXCLUSION}
                  onChange={handleChange}
                  rows={5}
                  placeholder="List exclusion points…"
                />
                <div className="md:col-span-2">
                  <TextAreaField
                    label="Comments"
                    name="Comments"
                    value={formData.Comments}
                    onChange={handleChange}
                    rows={4}
                    placeholder="Add any study comments / notes…"
                  />
                </div>
              </div>
            </Section>

            {/* Milestones */}
            <Section
              title="Milestones"
              subtitle="Regulatory & startup timeline"
              icon={<CalendarDays className="w-3.5 h-3.5 text-purple-300" />}
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {milestoneKeys.map((key) => (
                  <DateField
                    key={key}
                    label={formatLabel(key)}
                    name={key}
                    value={toInputDate(formData[key])}
                    onChange={handleChange}
                  />
                ))}
              </div>
            </Section>
          </div>

          {/* Footer */}
          <div className="relative px-5 py-3 border-t border-white/10 bg-slate-950/50 backdrop-blur-xl flex items-center justify-end gap-2 shrink-0">
            <div className="hidden sm:flex items-center gap-1.5 mr-auto text-[10px] uppercase tracking-wider text-slate-400">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.7)] animate-pulse" />
              Editing study
            </div>
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-xl bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] text-sm font-medium text-slate-200 hover:text-white transition"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              className="group relative inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500 ring-1 ring-white/20 text-sm font-semibold text-white hover:shadow-[0_10px_30px_-5px_rgba(139,92,246,0.6)] active:scale-[0.98] transition-all overflow-hidden"
            >
              <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/25 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
              <Save className="w-4 h-4 relative" />
              <span className="relative">Save</span>
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

/* ------------------------------ Subcomponents ----------------------------- */

const Section = ({ title, subtitle, icon, children }) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.025] backdrop-blur-xl p-4 relative overflow-hidden">
    <div className="flex items-center gap-2 mb-3">
      <div className="w-7 h-7 rounded-lg bg-white/[0.04] border border-white/10 flex items-center justify-center">
        {icon}
      </div>
      <div className="min-w-0">
        <h3 className="text-[11px] uppercase tracking-[0.2em] font-semibold text-white leading-tight">
          {title}
        </h3>
        {subtitle && (
          <p className="text-[10px] text-slate-400 leading-tight">{subtitle}</p>
        )}
      </div>
      <span className="ml-auto h-px flex-1 bg-white/5" />
    </div>
    {children}
  </div>
);

const Field = ({ label, name, value, onChange, icon, placeholder }) => (
  <label className="block">
    <span className="block text-[10px] uppercase tracking-wider font-medium text-slate-400 mb-1">
      {label}
    </span>
    <div className="relative">
      {icon && (
        <span className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
          {icon}
        </span>
      )}
      <input
        type="text"
        name={name}
        value={value ?? ""}
        onChange={onChange}
        placeholder={placeholder}
        className={`w-full rounded-xl bg-white/[0.04] border border-white/10 ${
          icon ? "pl-9 pr-3" : "px-3"
        } py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-purple-400/50 focus:bg-white/[0.06] focus:ring-2 focus:ring-purple-500/20 transition`}
      />
    </div>
  </label>
);

const NumberField = ({ label, name, value, onChange, icon }) => (
  <label className="block">
    <span className="block text-[10px] uppercase tracking-wider font-medium text-slate-400 mb-1">
      {label}
    </span>
    <div className="relative">
      {icon && (
        <span className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
          {icon}
        </span>
      )}
      <input
        type="number"
        name={name}
        value={value ?? ""}
        onChange={onChange}
        min={0}
        className={`w-full rounded-xl bg-white/[0.04] border border-white/10 ${
          icon ? "pl-9 pr-3" : "px-3"
        } py-2 text-sm text-slate-100 focus:outline-none focus:border-purple-400/50 focus:bg-white/[0.06] focus:ring-2 focus:ring-purple-500/20 transition`}
      />
    </div>
  </label>
);

const TextAreaField = ({ label, name, value, onChange, rows = 4, placeholder }) => (
  <label className="block">
    <span className="block text-[10px] uppercase tracking-wider font-medium text-slate-400 mb-1">
      {label}
    </span>
    <textarea
      name={name}
      value={value ?? ""}
      onChange={onChange}
      rows={rows}
      placeholder={placeholder}
      className="w-full rounded-xl bg-white/[0.04] border border-white/10 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-purple-400/50 focus:bg-white/[0.06] focus:ring-2 focus:ring-purple-500/20 transition resize-y"
    />
  </label>
);

const ToggleField = ({ label, name, checked, onChange }) => (
  <div>
    <span className="block text-[10px] uppercase tracking-wider font-medium text-slate-400 mb-1">
      {label}
    </span>
    <label className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2 cursor-pointer hover:bg-white/[0.06] transition">
      <span
        className={`text-xs font-medium ${
          checked ? "text-emerald-300" : "text-slate-400"
        }`}
      >
        {checked ? "Accepted" : "Not set"}
      </span>
      <input
        type="checkbox"
        className="sr-only peer"
        name={name}
        checked={checked}
        onChange={onChange}
      />
      <div className="w-10 h-5 rounded-full bg-white/10 peer-checked:bg-gradient-to-r peer-checked:from-emerald-400 peer-checked:to-teal-400 peer-checked:shadow-[0_0_10px_rgba(52,211,153,0.5)] transition relative">
        <div className="absolute top-0.5 left-0.5 h-4 w-4 bg-white rounded-full shadow transition peer-checked:translate-x-5" />
      </div>
    </label>
  </div>
);

const DateField = ({ label, name, value, onChange }) => (
  <label className="block">
    <span className="block text-[10px] uppercase tracking-wider font-medium text-slate-400 mb-1">
      {label}
    </span>
    <div className="relative">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
        <CalendarDays className="w-4 h-4 text-slate-500" />
      </span>
      <input
        type="date"
        name={name}
        value={value || ""}
        onChange={onChange}
        className="w-full rounded-xl bg-white/[0.04] border border-white/10 pl-9 pr-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-purple-400/50 focus:bg-white/[0.06] focus:ring-2 focus:ring-purple-500/20 transition [color-scheme:dark]"
      />
    </div>
  </label>
);

const SelectField = ({ label, name, value, onChange, options }) => (
  <label className="block">
    <span className="block text-[10px] uppercase tracking-wider font-medium text-slate-400 mb-1">
      {label}
    </span>
    <div className="relative">
      <select
        name={name}
        value={value || ""}
        onChange={onChange}
        className="w-full appearance-none rounded-xl bg-white/[0.04] border border-white/10 pl-3 pr-9 py-2 text-sm text-slate-100 hover:bg-white/[0.06] focus:outline-none focus:border-purple-400/50 focus:ring-2 focus:ring-purple-500/20 cursor-pointer transition"
      >
        <option value="" className="bg-[#0b0820] text-slate-100">
          Select status
        </option>
        {options.map((opt) => (
          <option key={opt} value={opt} className="bg-[#0b0820] text-slate-100">
            {opt}
          </option>
        ))}
      </select>
      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
    </div>
  </label>
);

/* --------------------------------- Utils --------------------------------- */

function formatLabel(key) {
  return key.replace(/_/g, " ").replace(/\b(\w)/g, (c) => c.toUpperCase());
}

/**
 * Convert various date-like values to yyyy-mm-dd for <input type="date" />
 */
function toInputDate(val) {
  if (!val) return "";
  // Accept already-formatted yyyy-mm-dd
  if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
  // Try parsing other formats
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return "";
  const yyyy = String(d.getFullYear());
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export default EditStudyModal;
